import type { ApiResult } from "@/types";

/** 通用请求选项。body 为 FormData 时不设 Content-Type,交给浏览器加 boundary。 */
export interface RequestOpts {
  method?: string;
  headers?: Record<string, string>;
  body?: BodyInit | Record<string, unknown> | null;
  signal?: AbortSignal;
  credentials?: RequestCredentials;
  /** 请求超时(毫秒);默认 180s 兜底,防止后端卡死时 UI 无限转圈。已知长操作可调大。 */
  timeoutMs?: number;
  /** JSON API 默认严格解析；RIS/BibTeX 等导出必须显式选择文本。 */
  responseType?: "json" | "text";
}

const DEFAULT_TIMEOUT_MS = 180_000;

/** LLM 端点的超时(毫秒)。默认 180s 不够:1 核机器上实测期刊匹配跑到 195s,
 * 而 charge() 在后端返回前就已执行、客户端 abort 不会回滚——用户看到"请求超时"
 * 却已经被扣了积分。AI 深度五档还会让耗时成倍上涨,所以给足 5 分钟。 */
const LLM_TIMEOUT_MS = 300_000;

/** 走 LLM 的端点(耗时长、按深度倍率放大)。与 BILLABLE_RE 的区别:那个管余额徽章刷新,
 * 这个管超时时长与超时文案。 */
const LLM_ENDPOINT_RE =
  /^\/api\/(interpret\/|narrative-report|rebuttal\/split|literature\/brief|journal\/match|reference\/check|cover-letter|stats-copilot|checkup\/[^/]+\/ask)/;

/** 计费端点(POST 成功即已扣积分;402 说明余额不足)。命中后派发事件,
 * AuthContext 据此刷新顶栏积分徽章——一处拦截,免去每个工具页手工调 refresh。 */
const BILLABLE_PATTERNS = [
  /^\/api\/checkup(?:[?]|$)/,
  /^\/api\/checkup\/[^/]+\/ask(?:[/?]|$)/,
  /^\/api\/(scan|compare|copy-move|dedup|cross-ref|stats-check|data-check|stats-copilot|interpret|narrative-report|rebuttal|literature|journal|reference|cover-letter)(?:[/?]|$)/,
] as const;

/** 供回归测试与超时提示共用，防止新增计费端点后忘记刷新全局积分余额。 */
export function isBillableEndpoint(url: string): boolean {
  return BILLABLE_PATTERNS.some((pattern) => pattern.test(url));
}

export const CREDITS_CHANGED_EVENT = "rg:credits-changed";

function notifyCreditsMaybeChanged(url: string, method: string, ok: boolean, status: number,
                                   outcomeUnknown = false): boolean {
  if (method !== "POST" || !isBillableEndpoint(url)) return false;
  if (!ok && status !== 402 && !outcomeUnknown) return false; // 明确的 400/401/5xx 无扣费证据,无需刷新
  try {
    window.dispatchEvent(new CustomEvent(CREDITS_CHANGED_EVENT));
    return true;
  } catch {
    /* 无 window 的环境(测试)静默 */
    return false;
  }
}

function isPlainBody(body: unknown): body is BodyInit {
  return (
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    ArrayBuffer.isView(body) ||
    body instanceof URLSearchParams ||
    body instanceof ReadableStream ||
    typeof body === "string" ||
    body === undefined ||
    body === null
  );
}

/**
 * 统一 fetch 封装:永不 throw(网络异常/超时/JSON 解析失败全部转换为 ApiResult 失败态),
 * 避免未捕获 rejection 在控制台冒泡为 error —— 这对静态环境冒烟测试(零 console error)尤其重要。
 */
export async function apiFetch<T>(url: string, opts: RequestOpts = {}): Promise<ApiResult<T>> {
  const isLlm = LLM_ENDPOINT_RE.test(url);
  const {
    method = "GET", headers = {}, body, signal, credentials, responseType = "json",
    // 调用方显式传了就听它的;否则 LLM 端点自动用更长的超时,别让页面各自记得传参数。
    timeoutMs = isLlm ? LLM_TIMEOUT_MS : DEFAULT_TIMEOUT_MS,
  } = opts;
  let finalBody: BodyInit | null | undefined;
  const finalHeaders: Record<string, string> = { ...headers };

  // 超时兜底:自建 controller,叠加调用方传入的 signal——任一触发即中断,避免后端卡死时 UI 无限转圈。
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  let creditsRefreshNotified = false;
  const onAbort = () => controller.abort(signal?.reason);
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", onAbort, { once: true });
  }

  try {
    if (isPlainBody(body)) {
      finalBody = body;
    } else if (body != null) {
      finalBody = JSON.stringify(body);
      finalHeaders["Content-Type"] = finalHeaders["Content-Type"] || "application/json";
    }
    const res = await fetch(url, {
      method,
      headers: finalHeaders,
      body: finalBody,
      signal: controller.signal,
      credentials: credentials ?? "same-origin",
    });

    creditsRefreshNotified = notifyCreditsMaybeChanged(url, method.toUpperCase(), res.ok, res.status);

    let payload: unknown = null;
    if (res.status !== 204 && method.toUpperCase() !== "HEAD") {
      // 读取异常必须交给外层取消/网络处理；吞掉它会把中断的响应当成成功。
      const text = await res.text();
      if (res.ok && responseType === "text") {
        payload = text;
      } else {
        try {
          payload = JSON.parse(text);
        } catch {
          if (res.ok) {
            return {
              ok: false,
              error: "服务返回了无效响应，请稍后重试",
              code: "invalid_response",
              retryable: true,
              status: res.status,
            };
          }
        }
      }
    }

    if (!res.ok) {
      const errObj = (payload && typeof payload === "object" ? (payload as Record<string, unknown>) : {}) as Record<
        string,
        unknown
      >;
      // FastAPI 的 HTTPException 用 `detail` 字段(字符串);校验错误时是数组。
      // 之前只读 `error`,导致后端精心写的中文报错(如"课题不存在")丢失、
      // 退化成英文 statusText("Not Found")。这里优先取 detail/error。
      // Only this exact pre-admission contract can turn a POST 404 into a safe rejection.
      const nested = errObj.detail && typeof errObj.detail === "object" && !Array.isArray(errObj.detail)
        ? errObj.detail as Record<string, unknown> : null;
      const projectRejected = res.status === 404 && nested?.code === "project_unavailable"
        && nested.acceptance === "rejected";
      let detailMsg: string | undefined;
      if (projectRejected && typeof nested?.detail === "string") {
        detailMsg = nested.detail;
      } else if (typeof nested?.detail === "string") {
        detailMsg = nested.detail;
      } else if (typeof errObj.detail === "string") {
        detailMsg = errObj.detail; // 我们自定义的 HTTPException(中文)
      } else if (Array.isArray(errObj.detail)) {
        // FastAPI 校验错误(422)detail 是数组,msg 是英文(如 "Field required")。
        // 对中文产品别直接抛英文——给友好中文兜底。
        detailMsg = "请求参数有误(有必填项缺失或格式不对)";
      }
      return {
        ok: false,
        error:
          (typeof errObj.error === "string" && errObj.error) ||
          detailMsg ||
          res.statusText ||
          `请求失败(${res.status})`,
        code: projectRejected ? "project_unavailable" : typeof nested?.code === "string" ? nested.code : typeof errObj.code === "string" ? errObj.code : res.status === 429 ? "quota_exceeded" : undefined,
        ...(projectRejected ? { acceptance: "rejected" as const } : {}),
        retryable: typeof errObj.retryable === "boolean" ? errObj.retryable : res.status >= 500,
        status: res.status,
      };
    }
    return { ok: true, data: payload as T };
  } catch (err) {
    const userAborted = !timedOut && (signal?.aborted || (err instanceof Error && err.name === "AbortError"));
    const billableOutcomeUnknown = timedOut && method.toUpperCase() === "POST" && isBillableEndpoint(url);
    if (billableOutcomeUnknown && !creditsRefreshNotified) {
      // The server may have charged before the response timed out. Refresh the balance once;
      // never create a second refresh if response headers already triggered it above.
      notifyCreditsMaybeChanged(url, method.toUpperCase(), false, 0, true);
    }
    return {
      ok: false,
      error: timedOut
        // A timed-out billable POST may already be accepted/charged. Tell the owner to
        // inspect history before retrying; aborting the browser request cannot roll it back.
        ? (billableOutcomeUnknown
            ? "等待超时了,但这次可能已经跑完并扣了积分——先去「我的记录」看看有没有结果,别急着重跑(重跑会再次扣积分)。"
            : "请求超时,服务可能繁忙,请稍后重试")
        : userAborted
          ? "请求已取消"
          : "网络连接失败,请检查网络后重试",
      ...(billableOutcomeUnknown ? { code: "billable_outcome_unknown", acceptance: "unknown" as const } : {}),
      retryable: !userAborted && !billableOutcomeUnknown, // uncertain billing must be reconciled before any new request
    };
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
  }
}

/** 便捷 JSON GET/POST/PATCH/DELETE。 */
export const http = {
  get: <T>(url: string, opts?: RequestOpts) => apiFetch<T>(url, { ...opts, method: "GET" }),
  post: <T>(url: string, body?: RequestOpts["body"], opts?: RequestOpts) =>
    apiFetch<T>(url, { ...opts, method: "POST", body }),
  patch: <T>(url: string, body?: RequestOpts["body"], opts?: RequestOpts) =>
    apiFetch<T>(url, { ...opts, method: "PATCH", body }),
  del: <T>(url: string, opts?: RequestOpts) => apiFetch<T>(url, { ...opts, method: "DELETE" }),
};
